import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Tag, BookOpen } from 'lucide-react';
import SEO from '../components/SEO';
import blogArticles from '../data/blogArticles';

const BlogPage = () => (
  <div className="pt-20">
    <SEO
      title="Blog – KI-Wissen für Unternehmen"
      description="Praxiswissen rund um KI-Prozessautomatisierung, KI im Mittelstand und Kosten von KI-Lösungen. Fachbeiträge von LAIzyones, Ihrer KI-Agentur aus Magdeburg."
      path="/blog"
      keywords="KI Blog, KI Wissen Unternehmen, KI Prozessautomatisierung, KI Mittelstand, künstliche Intelligenz Praxis, KI Ratgeber"
      breadcrumbs={[
        { name: 'Startseite', url: '/' },
        { name: 'Blog' },
      ]}
    />
    <HeroSection />
    <ArticlesGrid />
    <CTASection />
  </div>
);

const HeroSection = () => (
  <section className="section bg-gray-50 relative overflow-hidden">
    <div className="absolute inset-0 grid-pattern" />
    <div className="container-custom relative z-10">
      <div className="max-w-4xl mx-auto text-center">
        <div className="badge mb-6 fade-up">
          <BookOpen className="w-3.5 h-3.5" />
          KI-Wissen
        </div>
        <div className="reveal-container">
          <h1 className="heading-xl text-4xl md:text-5xl lg:text-6xl text-gray-900 reveal-text mb-6">
            Unser <span className="gradient-text">KI-Blog</span> für Unternehmen
          </h1>
        </div>
        <p className="body-text text-base md:text-lg text-gray-600 max-w-3xl mx-auto fade-up">
          Praxisnahe Einblicke, konkrete Beispiele und strategisches Wissen rund um
          KI-Prozessautomatisierung, künstliche Intelligenz im Mittelstand und die
          Kosten von KI-Lösungen — von den Experten bei LAIzyones.
        </p>
      </div>
    </div>
  </section>
);

const ArticlesGrid = () => (
  <section className="section bg-white">
    <div className="container-custom">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {blogArticles.map((article, i) => (
          <Link
            key={article.slug}
            to={`/blog/${article.slug}`}
            data-testid={`blog-card-${i}`}
            className="group block"
          >
            <article className="card h-full flex flex-col animate-card hover:border-emerald-200 transition-all duration-300">
              <div className="flex items-center gap-3 mb-4">
                <span className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full">
                  <Tag className="w-3 h-3" />
                  {article.category}
                </span>
                <span className="flex items-center gap-1 text-xs text-gray-400">
                  <Clock className="w-3 h-3" />
                  {article.readTime}
                </span>
              </div>

              <h2 className="font-['Space_Grotesk'] font-bold text-xl text-gray-900 mb-3 group-hover:text-emerald-600 transition-colors leading-tight">
                {article.title}
              </h2>

              <p className="text-gray-500 text-sm leading-relaxed mb-5 flex-1">
                {article.description}
              </p>

              <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-100">
                <time className="text-xs text-gray-400" dateTime={article.date}>
                  {new Date(article.date).toLocaleDateString('de-DE', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                </time>
                <span className="inline-flex items-center gap-1 text-emerald-600 font-semibold text-sm group-hover:gap-2 transition-all duration-300">
                  Lesen <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </article>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

const CTASection = () => (
  <section className="section bg-gray-50 pb-24 md:pb-32">
    <div className="container-custom">
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0A0F14] to-[#111820] p-8 md:p-12 lg:p-16">
        <div className="absolute inset-0 grid-pattern opacity-10" />
        <div className="relative z-10 text-center max-w-3xl mx-auto">
          <h2 className="font-['Space_Grotesk'] font-bold text-3xl md:text-4xl text-white mb-6">
            Bereit, KI in Ihrem Unternehmen <span className="text-emerald-400">einzusetzen</span>?
          </h2>
          <p className="text-gray-400 text-base md:text-lg mb-8">
            Theorie ist gut — Praxis ist besser. Starten Sie mit einer kostenlosen
            Prozessanalyse und erfahren Sie, wo KI in Ihrem Unternehmen den größten Hebel hat.
          </p>
          <Link
            to="/#kontakt"
            data-testid="blog-cta"
            className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold px-8 py-4 rounded-xl hover:from-emerald-600 hover:to-cyan-600 transition-all"
          >
            Kostenlose Beratung anfragen <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </div>
  </section>
);

export default BlogPage;
